// Recurring Invoice — one profile, Zoho Books style (spec §4): the facts on the
// left, the schedule, lines, invoices raised and history on the right.
import React, { useState } from "react";
import { Avatar, Btn, Fact, IC, RowBtn, SectHead, StatusPill, TypeTag } from "./RecurringParts";
import { cap, compact, cycleWord, dBetween, daysTo, fmtD, lineOf, money, money2, monthlyValue, pISO, sumNet, today } from "./recurringModel";

const ProfileView = ({ p, invoices, permit, cur, act, busy }) => {
  const [tab, setTab] = useState("overview");
  if (!p) return null;
  const inv = invoices.filter((x) => x.profileGuid === p.guid).sort((a, b) => (a.date < b.date ? 1 : -1));
  const paid = inv.filter((x) => x.pay === "Paid");
  const pending = inv.filter((x) => x.pay === "Pending");
  const nd = daysTo(p.next);

  const hist = [];
  if (p.history && p.history.length) {
    p.history.forEach((h) =>
      hist.push({ t: h.action || h.title || "", s: [h.note, h.user ? "by " + h.user : ""].filter(Boolean).join(" · "), d: (h.date || "").slice(0, 10), bad: /fail/i.test(h.action || "") })
    );
  } else {
    hist.push({ t: "Profile created", s: p.createdBy ? "by " + p.createdBy : "", d: p.date });
    if (inv.length) {
      const first = inv[inv.length - 1];
      hist.push({ t: "First invoice raised", s: first.no + " · " + money2(first.net), d: first.date, q: true });
      if (inv.length > 1) hist.push({ t: "Latest invoice raised", s: inv[0].no + " · " + money2(inv[0].net) + " · " + inv[0].pay, d: inv[0].date, q: true });
    }
    if (p.failed) hist.push({ t: "Generation failed", s: p.lastRunMessage || "the scheduler could not raise the invoice", d: (p.lastRunAt || "").slice(0, 10), bad: true });
    if (p.next) hist.push({ t: "Next invoice due", s: cap(cycleWord(p)), d: p.next, q: true });
  }
  hist.sort((a, b) => (a.d < b.d ? 1 : -1));

  return (
    <section className="axrc-screen">
      <div className="axrc-phead">
        <div>
          <h1>{p.no || "Recurring profile"}</h1>
          <div className="sub">
            {[p.service, p.type, cap(cycleWord(p))].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="sp" />
        <Btn icon={IC.back} onClick={act.back}>
          Back to list
        </Btn>
        {permit.edit && (
          <Btn icon={IC.edit} onClick={() => act.edit(p)}>
            Edit
          </Btn>
        )}
        {permit.edit && (p.status === "Active" || p.status === "Paused") && (
          <Btn onClick={() => act.pause(p)} disabled={busy}>
            {p.status === "Paused" ? "Resume" : "Pause"}
          </Btn>
        )}
        {permit.add && <Btn onClick={() => act.clone(p)}>Clone</Btn>}
        {permit.add && p.status === "Active" && (
          <Btn kind="grn" icon={IC.play} onClick={() => act.makeNow(p)} disabled={busy}>
            Create invoice now
          </Btn>
        )}
      </div>

      <div className="axrc-viewwrap">
        <div className="axrc-card axrc-vside">
          <div className="sc">
            <SectHead>Profile</SectHead>
            <dl className="axrc-dl">
              <dt>Profile no.</dt>
              <dd className="mono">{p.no || "—"}</dd>
              <dt>Date</dt>
              <dd>{fmtD(p.date)}</dd>
              <dt>Customer</dt>
              <dd>{p.custName || "—"}</dd>
              <dt>Salesperson</dt>
              <dd>{p.salesperson || "—"}</dd>
              <dt>Warehouse</dt>
              <dd>{p.warehouse || "—"}</dd>
              <dt>Main service</dt>
              <dd>{p.service || "—"}</dd>
              <dt>Invoice type</dt>
              <dd>
                <TypeTag t={p.type} />
              </dd>
              <dt>Payment terms</dt>
              <dd>{p.terms}</dd>
              <dt>Auto email</dt>
              <dd>{p.autoEmail ? "Yes · " + (p.email || "no address") : "No"}</dd>
              <dt>Status</dt>
              <dd>
                <StatusPill s={p.status} />
              </dd>
            </dl>
            <SectHead>Billing so far</SectHead>
            <div className="axrc-facts">
              <Fact l="Invoices raised" v={inv.length} s={"since " + fmtD(p.start)} />
              <Fact l="Billed" v={cur + " " + compact(sumNet(inv))} s="loaded period" />
              <Fact l="Collected" v={cur + " " + compact(sumNet(paid))} cls="good" s={paid.length + " invoices"} />
              <Fact l="Outstanding" v={cur + " " + compact(sumNet(pending))} cls="warn" s={pending.length + " invoices"} />
            </div>
          </div>
        </div>

        <div className="axrc-card axrc-vmain">
          <div className="axrc-vhead">
            <Avatar name={p.custName} big />
            <div className="tx">
              <div className="t">
                <span>{p.custName}</span>
                <StatusPill s={p.status} />
                {p.failed ? (
                  <span className="axrc-pill failed" title={p.lastRunMessage}>
                    <span className="d" />
                    Last run failed
                  </span>
                ) : null}
              </div>
              <div className="s">{[p.custPhone, p.custEmail].filter(Boolean).join(" · ") || "—"}</div>
            </div>
          </div>
          <nav className="axrc-vtabs">
            <button type="button" className={tab === "overview" ? "on" : ""} onClick={() => setTab("overview")}>
              Overview
            </button>
            <button type="button" className={tab === "invoices" ? "on" : ""} onClick={() => setTab("invoices")}>
              Invoices raised <span className="cnt">{inv.length}</span>
            </button>
            <button type="button" className={tab === "history" ? "on" : ""} onClick={() => setTab("history")}>
              History
            </button>
          </nav>
          <div className="axrc-vbody">
            {tab === "overview" && (
              <div>
                <div className="axrc-sched">
                  <div className="c">
                    <div className="l">Repeat</div>
                    <div className="v">{cap(cycleWord(p))}</div>
                    <div className="s">from {fmtD(p.start)}</div>
                  </div>
                  <div className="c next">
                    <div className="l">Next invoice</div>
                    <div className="v">{p.next ? fmtD(p.next) : "—"}</div>
                    <div className="s">{p.next ? (nd === 0 ? "today" : nd < 0 ? Math.abs(nd) + " days overdue" : "in " + nd + " days") : "nothing scheduled"}</div>
                  </div>
                  <div className="c">
                    <div className="l">Ends</div>
                    <div className="v">{p.end ? fmtD(p.end) : "Never"}</div>
                    <div className="s">{p.end ? Math.max(0, dBetween(today(), pISO(p.end))) + " days left" : "runs until cancelled"}</div>
                  </div>
                  <div className="c">
                    <div className="l">Per cycle</div>
                    <div className="v">{cur + " " + money(p.net)}</div>
                    <div className="s">{cur + " " + money(monthlyValue(p))} per month</div>
                  </div>
                </div>
                <SectHead>What gets billed every cycle</SectHead>
                <div className="axrc-vdoc">
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="num">Qty</th>
                        <th className="num">Rate</th>
                        <th className="num">Disc %</th>
                        <th className="num">Disc. rate</th>
                        <th className="num">Taxable</th>
                        <th className="num">VAT %</th>
                        <th className="num">VAT</th>
                        <th className="num">Net amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.items.map((l) => {
                        const x = lineOf(l);
                        return (
                          <tr key={l.key}>
                            <td>
                              <b>{l.productName}</b>
                              {l.note ? <div className="axrc-dim small">{l.note}</div> : null}
                            </td>
                            <td className="num">{l.qty}</td>
                            <td className="num">{money2(l.rate)}</td>
                            <td className="num">{l.disc ? l.disc + "%" : "—"}</td>
                            <td className="num">{money2(x.discRate)}</td>
                            <td className="num">{l.taxable ? money2(x.taxable) : "Exempt"}</td>
                            <td className="num">{l.taxable ? l.vatPct + "%" : "—"}</td>
                            <td className="num">{money2(x.vat)}</td>
                            <td className="num strong">{money2(x.net)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="axrc-vtot">
                  <div className="box">
                    <div className="r">
                      <span className="k">Taxable total</span>
                      <span className="v">{money2(p.taxable)}</span>
                    </div>
                    <div className="r">
                      <span className="k">VAT total</span>
                      <span className="v">{money2(p.vat)}</span>
                    </div>
                    <div className="r net">
                      <span className="k">Net total</span>
                      <span className="v">{cur + " " + money2(p.net)}</span>
                    </div>
                  </div>
                </div>
                <SectHead>Notes</SectHead>
                <div className="axrc-notes">{p.notes || "No notes on this profile."}</div>
              </div>
            )}
            {tab === "invoices" &&
              (inv.length ? (
                <div className="axrc-vdoc">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Date</th>
                        <th>Due</th>
                        <th className="num">Amount</th>
                        <th>Payment</th>
                        <th className="num">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inv.map((x) => (
                        <tr key={x.guid}>
                          <td className="mono">{x.no}</td>
                          <td>{fmtD(x.date)}</td>
                          <td>{fmtD(x.due)}</td>
                          <td className="num">{money2(x.net)}</td>
                          <td>
                            <StatusPill s={x.pay} /> {x.pay === "Paid" && x.mode ? <span className="axrc-dim small">{x.mode}</span> : null}
                          </td>
                          <td className="num">
                            <span className="axrc-rowacts">
                              <RowBtn title="View" icon={IC.view} onClick={() => act.viewInvoice(x)} />
                              {permit.add && x.pay === "Pending" && <RowBtn kind="pay" title="Receive payment & make invoice" icon={IC.cash} onClick={() => act.receive(x)} />}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="axrc-nores">No invoice has been raised from this profile yet.</div>
              ))}
            {tab === "history" && (
              <div className="axrc-timeline">
                {hist.map((x, i) => (
                  <div key={i} className={"tl" + (x.bad ? " bad" : x.q ? " q" : "")}>
                    <div className="t">{x.t}</div>
                    {x.s ? <div className="s">{x.s}</div> : null}
                    <div className="d">{fmtD(x.d)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileView;
